"use client";

import React, { createContext, useContext, useEffect, useState, useMemo, useCallback, useRef } from "react";
import { useRouter } from "@/i18n/navigation";
import { createClient } from "@/backend/lib/supabase/client";
import type { User as SupabaseUser } from "@supabase/supabase-js";
import { isLogoutRoute, isAuthPage } from "@/frontend/config/routes";
import { useSetAtom } from "jotai";
import { userAtom, authStatusAtom, accessTokenAtom, userRoleAtom, resetAtomStore } from "@lib/atoms";
import type { SystemRole } from "@/isomorphic/schemas/profile";

interface AuthState {
  user: SupabaseUser | null;
  loading: boolean;
  error: string | null;
}

interface AuthContextType extends AuthState {
  signOut: () => Promise<void>;
  getToken: () => Promise<string | null>;
  clearSession: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);
const SIGNED_OUT_MARKER_KEY = "pacelinker.auth.signed-out";

function hasSignedOutMarker() {
  if (typeof window === "undefined") {
    return false;
  }

  return window.sessionStorage.getItem(SIGNED_OUT_MARKER_KEY) === "1";
}

function setSignedOutMarker(isSignedOut: boolean) {
  if (typeof window === "undefined") {
    return;
  }

  if (isSignedOut) {
    window.sessionStorage.setItem(SIGNED_OUT_MARKER_KEY, "1");
    return;
  }

  window.sessionStorage.removeItem(SIGNED_OUT_MARKER_KEY);
}

export function AuthProvider({
  children,
  initialUser = null,
}: {
  children: React.ReactNode;
  initialUser?: SupabaseUser | null;
}) {
  const setUser = useSetAtom(userAtom);
  const setAuthStatus = useSetAtom(authStatusAtom);
  const setAccessToken = useSetAtom(accessTokenAtom);
  const setUserRole = useSetAtom(userRoleAtom);
  const [allowServerAuthBootstrap, setAllowServerAuthBootstrap] = useState(() => !hasSignedOutMarker());
  const bootstrappedInitialUser = allowServerAuthBootstrap ? initialUser : null;
  const [authState, setAuthState] = useState<AuthState>({
    user: bootstrappedInitialUser,
    loading: false,
    error: null,
  });
  const currentUserRef = useRef<SupabaseUser | null>(bootstrappedInitialUser);
  const router = useRouter();
  // Use useMemo to create Supabase client only once to avoid reference changes
  const supabase = useMemo(() => createClient(), []);
  const fetchUserRole = useCallback(
    async (accessToken: string | null, user: SupabaseUser | null) => {
      if (!user || isLogoutRoute(window.location.pathname)) {
        setUserRole(null);
        return;
      }

      try {
        const headers = accessToken
          ? {
              Authorization: `Bearer ${accessToken}`,
            }
          : undefined;
        const response = await fetch("/api/user/role", {
          headers,
        });

        if (!response.ok) {
          throw new Error("Failed to fetch role");
        }

        const roleData = (await response.json()) as { data?: { role?: SystemRole } };
        setUserRole(roleData.data?.role || "user");
      } catch (error) {
        console.error("[AuthProvider] Error fetching user role:", error);
        setUserRole("user");
      }
    },
    [setUserRole],
  );

  useEffect(() => {
    currentUserRef.current = authState.user;
  }, [authState.user]);

  useEffect(() => {
    setUser(bootstrappedInitialUser);
    setAccessToken(null);
    setAuthStatus(bootstrappedInitialUser ? "authenticated" : "unauthenticated");
    setUserRole(null);
  }, [bootstrappedInitialUser, setAccessToken, setAuthStatus, setUser, setUserRole]);

  useEffect(() => {
    const syncInitialSession = async () => {
      try {
        const {
          data: { session },
          error,
        } = await supabase.auth.getSession();

        if (error) {
          console.error("Error getting session:", error);
          setUser(null);
          setAccessToken(null);
          setAuthStatus("unauthenticated");
          setUserRole(null);
          setAuthState({ user: null, loading: false, error: error.message });
          return;
        }

        const user = session?.user || bootstrappedInitialUser || null;
        const accessToken = session?.access_token || null;

        // After a client-side login, onAuthStateChange can populate the in-memory user
        // slightly before getSession() reflects it. Avoid clearing that user back to null.
        if (!user && currentUserRef.current && !hasSignedOutMarker()) {
          return;
        }

        setUser(user);
        setAccessToken(accessToken);
        setAuthStatus(user ? "authenticated" : "unauthenticated");
        if (user) {
          setSignedOutMarker(false);
          setAllowServerAuthBootstrap(true);
        }
        setAuthState({
          user,
          loading: false,
          error: null,
        });

        void fetchUserRole(accessToken, user);
      } catch (error) {
        console.error("Error in getInitialSession:", error);
        setAuthState({
          user: null,
          loading: false,
          error: error instanceof Error ? error.message : "Unknown error",
        });
      }
    };

    void syncInitialSession();
  }, [bootstrappedInitialUser, fetchUserRole, setAccessToken, setAuthStatus, setUser, setUserRole, supabase]);

  // useEffect 2: Auth state listener (always active, independent of initialization)
  useEffect(() => {
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (event, session) => {
      const user = session?.user || null;
      const accessToken = session?.access_token || null;
      if (event === "SIGNED_OUT") {
        setSignedOutMarker(true);
        setAllowServerAuthBootstrap(false);
      } else if (event === "SIGNED_IN" || event === "INITIAL_SESSION" || event === "TOKEN_REFRESHED") {
        setSignedOutMarker(false);
        setAllowServerAuthBootstrap(true);
      }
      setUser(user);
      setAccessToken(accessToken);
      setAuthStatus(user ? "authenticated" : "unauthenticated");
      setAuthState({
        user,
        loading: false,
        error: null,
      });

      void fetchUserRole(accessToken, user);

      // Redirect to login if user signs out
      // Always redirect to login WITHOUT redirect parameter (user intentionally logged out)
      if (event === "SIGNED_OUT") {
        // Reset ALL atoms at once for security (clears profile, workspaces, agents, etc.)
        resetAtomStore();

        const pathname = window.location.pathname;
        const onAuthPage = isAuthPage(pathname);

        // Only redirect if not already on an auth page
        if (!onAuthPage) {
          const locale = pathname.split("/")[1] || "fr";
          router.push(`/${locale}/auth/login`);
        }
      }
    });

    return () => {
      subscription.unsubscribe();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [supabase]);

  const signOut = async () => {
    try {
      const { error } = await supabase.auth.signOut();
      if (error) {
        console.error("Error signing out:", error);
        throw error;
      }
      // Note: State update and redirection are handled by onAuthStateChange listener (SIGNED_OUT event)
    } catch (error) {
      console.error("Error in signOut:", error);
      setAuthState((prev) => ({
        ...prev,
        error: error instanceof Error ? error.message : "Unknown error",
      }));
    }
  };

  const clearSession = async () => {
    try {
      setSignedOutMarker(true);
      setAllowServerAuthBootstrap(false);
      await supabase.auth.signOut();
      setUser(null);
      setAccessToken(null);
      setAuthStatus("unauthenticated");
      setUserRole(null);
      setAuthState({
        user: null,
        loading: false,
        error: null,
      });
    } catch (error) {
      console.error("Error clearing session:", error);
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user: authState.user,
        loading: authState.loading,
        error: authState.error,
        signOut,
        clearSession,
        getToken: async () => {
          const {
            data: { session },
            error,
          } = await supabase.auth.getSession();
          if (error) {
            console.error("Error getting session:", error);
            return null;
          }

          if (session?.access_token) {
            return session.access_token;
          }

          const {
            data: { session: refreshedSession },
            error: refreshError,
          } = await supabase.auth.refreshSession();

          if (refreshError) {
            console.error("Error refreshing session:", refreshError);
            return null;
          }

          return refreshedSession?.access_token || null;
        },
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
